import type { ContentBlock } from '@/types/flow';
import { Phone, Video, Info, ThumbsUp, Image, Smile, Mic, Shuffle } from 'lucide-react';
import type { BroadcastUtilityTemplate } from '@/services/api/broadcast';

interface MessengerPreviewProps {
    blocks: ContentBlock[];
    pageName?: string;
    utilityTemplate?: BroadcastUtilityTemplate | null;
}

export default function MessengerPreview({ blocks, pageName, utilityTemplate }: MessengerPreviewProps) {
    const initials = pageName ? pageName.substring(0, 2).toUpperCase() : 'FB';
    const initial = pageName ? pageName.substring(0, 1).toUpperCase() : 'P';

    return (
        <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white flex flex-col" style={{ height: '520px' }}>
            {/* Messenger Header */}
            <div className="bg-white px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <div className="relative">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-xs">{initials}</span>
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-900 leading-tight">{pageName || 'Sua Página'}</p>
                        <p className="text-[10px] text-green-600 font-medium">Ativo agora</p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600">
                        <Phone className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600">
                        <Video className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600">
                        <Info className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {/* Chat area */}
            <div className="flex-1 bg-white px-3 py-3 overflow-y-auto space-y-1.5">
                {utilityTemplate ? (
                    <UtilityTemplatePreview template={utilityTemplate} initial={initial} />
                ) : blocks.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-xs text-gray-300">Sem conteúdo</p>
                    </div>
                ) : (
                    <>
                        {/* Date separator */}
                        <div className="flex items-center justify-center mb-2">
                            <span className="text-[10px] text-gray-400 font-medium">
                                {new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).toUpperCase()}
                            </span>
                        </div>

                        {blocks.map((block, index) => (
                            <div key={index} className="flex items-end gap-1.5">
                                {/* Avatar on first or last message */}
                                {index === blocks.length - 1 ? (
                                    <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0">
                                        <span className="text-white font-bold text-[10px]">{initial}</span>
                                    </div>
                                ) : (
                                    <div className="w-7 shrink-0" />
                                )}

                                <div className="max-w-[220px]">
                                    {/* Text */}
                                    {block.type === 'text' && block.message && (
                                        <div className={`bg-[#e4e6eb] px-3 py-2 ${index === 0 && blocks.length === 1
                                            ? 'rounded-2xl rounded-bl-md'
                                            : index === 0
                                                ? 'rounded-2xl rounded-bl-md'
                                                : index === blocks.length - 1
                                                    ? 'rounded-2xl rounded-tl-md'
                                                    : 'rounded-2xl rounded-l-md'
                                            }`}>
                                            <p className="text-[13px] text-gray-900 whitespace-pre-wrap break-words leading-snug">
                                                {block.message}
                                            </p>
                                        </div>
                                    )}
                                    {block.type === 'text' && !block.message && (
                                        <div className="bg-[#e4e6eb] rounded-2xl rounded-bl-md px-3 py-2 opacity-40">
                                            <p className="text-[13px] text-gray-400 italic">Texto vazio</p>
                                        </div>
                                    )}

                                    {/* Image */}
                                    {block.type === 'image' && (
                                        <div className="space-y-1">
                                            <div className={`overflow-hidden ${index === 0 ? 'rounded-2xl rounded-bl-md' : 'rounded-2xl rounded-tl-md'
                                                }`}>
                                                {block.media_url ? (
                                                    <img
                                                        src={block.media_url}
                                                        alt="Preview"
                                                        className="w-full h-40 object-cover"
                                                        onError={(e) => {
                                                            e.currentTarget.src = 'https://via.placeholder.com/220x160?text=Imagem';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-28 bg-gray-100 flex items-center justify-center">
                                                        <Image className="w-6 h-6 text-gray-300" strokeWidth={1.5} />
                                                    </div>
                                                )}
                                                {(block.title || block.subtitle) && (
                                                    <div className="bg-white border border-gray-100 px-3 py-2">
                                                        {block.title && (
                                                            <p className="text-[13px] font-semibold text-gray-900 line-clamp-2">{block.title}</p>
                                                        )}
                                                        {block.subtitle && (
                                                            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{block.subtitle}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {block.buttons && block.buttons.length > 0 && (
                                                <div className="space-y-0.5">
                                                    {block.buttons.map((btn, btnIdx) => (
                                                        <button
                                                            key={btnIdx}
                                                            className="w-full bg-white border border-gray-200 rounded-lg py-1.5 px-3 text-[13px] font-medium text-[#0084ff] hover:bg-blue-50 transition-colors flex items-center justify-center gap-1.5"
                                                        >
                                                            {btn.text || 'Botão'}
                                                            {btn.type === 'url' && btn.url_entries && btn.url_entries.length > 1 && (
                                                                <span className="flex items-center gap-0.5 text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-semibold">
                                                                    <Shuffle className="w-2.5 h-2.5" />
                                                                    {btn.url_entries.length}
                                                                </span>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Video */}
                                    {block.type === 'video' && (
                                        <div className={`overflow-hidden bg-gray-900 ${index === 0 ? 'rounded-2xl rounded-bl-md' : 'rounded-2xl rounded-tl-md'
                                            }`}>
                                            <div className="w-full h-40 flex items-center justify-center">
                                                <div className="text-center text-white">
                                                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-1">
                                                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                            <path d="M8 5v14l11-7z" />
                                                        </svg>
                                                    </div>
                                                    <p className="text-[11px] text-white/70">Vídeo</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Audio */}
                                    {block.type === 'audio' && (
                                        <div className="bg-[#e4e6eb] rounded-2xl rounded-bl-md p-3 flex items-center gap-2.5">
                                            <div className="w-8 h-8 bg-[#0084ff] rounded-full flex items-center justify-center shrink-0">
                                                <Mic className="w-4 h-4 text-white" strokeWidth={1.5} />
                                            </div>
                                            <div className="flex-1">
                                                <div className="h-1 bg-gray-300 rounded-full">
                                                    <div className="h-1 bg-[#0084ff] rounded-full w-1/3" />
                                                </div>
                                                <p className="text-[10px] text-gray-500 mt-1">0:00 / 0:30</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Text block buttons */}
                                    {block.type === 'text' && block.buttons && block.buttons.length > 0 && (
                                        <div className="space-y-0.5 mt-1">
                                            {block.buttons.map((btn, btnIdx) => (
                                                <button
                                                    key={btnIdx}
                                                    className="w-full bg-white border border-gray-200 rounded-lg py-1.5 px-3 text-[13px] font-medium text-[#0084ff] hover:bg-blue-50 transition-colors flex items-center justify-center gap-1.5"
                                                >
                                                    {btn.text || 'Botão'}
                                                    {btn.type === 'url' && btn.url_entries && btn.url_entries.length > 1 && (
                                                        <span className="flex items-center gap-0.5 text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-semibold">
                                                            <Shuffle className="w-2.5 h-2.5" />
                                                            {btn.url_entries.length}
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Seen indicator */}
                        <div className="flex justify-start ml-9">
                            <span className="text-[10px] text-gray-400">
                                {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </>
                )}
            </div>

            {/* Messenger Footer - Input bar */}
            <div className="bg-white px-2 py-2 border-t border-gray-100">
                <div className="flex items-center gap-1">
                    <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600 shrink-0">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
                            <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="1.5" />
                            <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                    </button>
                    <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600 shrink-0">
                        <Image className="w-4.5 h-4.5" strokeWidth={1.5} />
                    </button>
                    <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600 shrink-0">
                        <Smile className="w-4.5 h-4.5" strokeWidth={1.5} />
                    </button>
                    <div className="flex-1 bg-gray-100 rounded-full px-3 py-1.5 mx-0.5">
                        <p className="text-[13px] text-gray-400">Aa</p>
                    </div>
                    <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600 shrink-0">
                        <ThumbsUp className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function UtilityTemplatePreview({
    template,
    initial,
}: { template: BroadcastUtilityTemplate; initial: string }) {
    const buttons = (() => {
        const raw = template.buttons_json;
        if (!raw) return [];
        try {
            const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
            return Array.isArray(arr) ? arr : [];
        } catch {
            return [];
        }
    })();
    const firstUrl = template.approved_url_examples[0];
    const bodyRaw = template.text_content || template.subtitle || '';
    const bodyText = bodyRaw.replace(/\{\{first_name\}\}/gi, 'Junior');

    return (
        <>
            <div className="flex items-center justify-center mb-2">
                <span className="text-[10px] text-gray-400 font-medium">
                    {new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).toUpperCase()}
                </span>
            </div>
            <div className="flex items-end gap-1.5">
                <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-[10px]">{initial}</span>
                </div>
                <div className="max-w-[240px] space-y-1">
                    {template.image_url && (
                        <div className="bg-[#e4e6eb] rounded-2xl rounded-bl-md overflow-hidden">
                            <img
                                src={template.image_url}
                                alt={template.title || ''}
                                className="w-full h-32 object-cover"
                            />
                            <div className="px-3 py-2">
                                <p className="text-[13px] text-gray-900 font-semibold leading-snug">{template.title}</p>
                                {bodyText && (
                                    <p className="text-[12px] text-gray-700 mt-1 leading-snug whitespace-pre-wrap break-words">
                                        {bodyText}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                    {!template.image_url && (
                        <div className="bg-[#e4e6eb] px-3 py-2 rounded-2xl rounded-bl-md">
                            <p className="text-[13px] text-gray-900 font-semibold leading-snug">{template.title}</p>
                            {bodyText && (
                                <p className="text-[12px] text-gray-700 mt-1 whitespace-pre-wrap break-words leading-snug">
                                    {bodyText}
                                </p>
                            )}
                        </div>
                    )}
                    {buttons.map((b: { title?: string; url?: string; type?: string; payload?: string }, i: number) => {
                        const isPb = b.type === 'postback';
                        return (
                            <button
                                key={i}
                                className={`w-full bg-white border rounded-lg py-1.5 px-3 text-[13px] font-medium transition-colors ${
                                    isPb
                                        ? 'border-gray-200 text-[#0084ff] hover:bg-blue-50'
                                        : 'border-gray-200 text-[#0084ff] hover:bg-blue-50'
                                }`}
                                title={isPb ? `postback: ${b.payload || ''}` : (firstUrl || b.url || '')}
                            >
                                {b.title || 'Ver detalhes'}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="mt-1 ml-9">
                <span className="text-[10px] text-emerald-600 font-medium">✓ Template UTILITY aprovado</span>
            </div>
        </>
    );
}
